import React, { Fragment, useCallback, useState, useMemo } from 'react';
import { Box, Container, Paper } from '@mui/material';
import DateFnsUtils from '@date-io/date-fns';
//
import FilterPanel from 'components/reports/analytics/FilterPanel';
import ReportTable from 'components/reports/analytics/ReportTable';
import Title from 'components/common/Title';
import { ReportsChart } from 'components/reports/analytics/ReportsChart';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_GROUP_BY_TYPE,
  REPORT_CHART_METRICS_TYPE,
} from '../../../constants/reports';
import { showErrorNotification } from 'utils/common';
import { styled } from '@mui/material/styles';
import { useBlobDownload } from 'hooks/utils/useBlobDownload';
import {
  useDownloadReportMutation,
  useGetChartDataQuery,
  useGetDailyReportQuery,
} from '../../../store/api/report.api';
import { useDefaultReportRange } from 'hooks/utils/useDefaultReportRange';
import { useApiErrorSnackbar } from 'hooks/utils/useApiErrorSnackbar';
const dateFns = new DateFnsUtils();

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  overflow: 'auto',
  flexDirection: 'column',
}));
const CHART_CONFIG_PLACEHOLDER = {
  chartConfig: { chartData: [], lines: [] },
};
const REPORT_PLACEHOLDER = {
  rows: [],
  count: 0,
};
const ReportsPage = () => {
  const { startDate, endDate } = useDefaultReportRange();
  const [paginationModel, setPaginationModel] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [currentFilters, setCurrentFilters] = useState({
    groupBy: DEFAULT_GROUP_BY_TYPE,
    startDate,
    endDate,
  });
  const [chartMetricsType, setChartMetricsType] = useState(
    REPORT_CHART_METRICS_TYPE.IMPRESSIONS,
  );
  // params,shared across all requests
  const coreFilterParams = useMemo(() => {
    const { groupBy, ts, startDate, endDate, ...filters } =
      currentFilters || {};
    return {
      groupBy,
      ts, // exposing timestamp prop to upper level, so it could be easily destructured and omitted later
      filters: {
        ...filters,
        startDate: dateFns.format(startDate, DEFAULT_DATE_FORMAT),
        endDate: dateFns.format(endDate, DEFAULT_DATE_FORMAT),
      },
    };
  }, [currentFilters]);
  // params for report downloading and displaying
  const downloadReportParams = useMemo(
    () => ({
      ...coreFilterParams,
      columns: ['imp', 'click', 'pixel', 'invalidImp', 'invalidClick'],
    }),
    [coreFilterParams],
  );
  // params for displaying reports - same as download + pagination
  const showReportParams = useMemo(
    () => ({
      ...downloadReportParams,
      page: paginationModel.pageIndex,
      limit: paginationModel.pageSize,
    }),
    [downloadReportParams, paginationModel.pageIndex, paginationModel.pageSize],
  );
  // params for chart display
  const showChartParams = useMemo(
    () => ({
      ...coreFilterParams,
      metric: chartMetricsType,
    }),
    [coreFilterParams, chartMetricsType],
  );
  const {
    data: { rows, count } = REPORT_PLACEHOLDER,
    error: reportError,
    isLoading: isReportsNotLoaded,
    isFetching: isLoadingReport,
  } = useGetDailyReportQuery(showReportParams);
  const {
    data: chartConfig = CHART_CONFIG_PLACEHOLDER,
    error: chartError,
    isFetching: isLoadingChart,
    isLoading: isChartsNotLoaded,
  } = useGetChartDataQuery(showChartParams);

  useApiErrorSnackbar(reportError);
  useApiErrorSnackbar(chartError);
  const [downloadReport, { isLoading: isDownloadingReport }] =
    useDownloadReportMutation();
  const downloadBlob = useBlobDownload();

  const isRequestPending =
    isLoadingChart || isLoadingReport || isDownloadingReport;

  const applyFilters = useCallback(
    (filtersProps) => {
      setCurrentFilters({ ...filtersProps, ts: Date.now() });
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setCurrentFilters, setPaginationModel],
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      const data = await downloadReport(downloadReportParams).unwrap();
      downloadBlob(data, 'report.csv');
    } catch (err) {
      if (err.response) {
        const blob = err.response.data;
        if (blob instanceof Blob) {
          const errorText = await blob.text();

          const parsedError = JSON.parse(errorText);

          showErrorNotification(parsedError.error);
        }
      } else {
        console.error(`Error download report: ${err.message}`);
      }
    }
  }, [downloadReport, downloadBlob, downloadReportParams]);

  return (
    <Fragment>
      <Container
        maxWidth='100%'
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <StyledPaper sx={{ overflow: 'hidden' }}>
          <Box>
            <Title>Reports</Title>
          </Box>
          <FilterPanel
            initialValues={currentFilters}
            applyFilters={applyFilters}
            isRequestPending={isRequestPending}
          />
        </StyledPaper>
        {isChartsNotLoaded ? null : (
          <StyledPaper sx={{ overflow: 'visible' }}>
            <ReportsChart
              chartConfig={chartConfig}
              isRequestPending={isLoadingChart}
              chartMetricsType={chartMetricsType}
              changeMetricsType={setChartMetricsType}
              groupBy={currentFilters.groupBy}
            />
          </StyledPaper>
        )}
        {isReportsNotLoaded ? null : (
          <Paper>
            <ReportTable
              groupBy={currentFilters.groupBy}
              rowData={rows}
              count={count}
              paginationModel={paginationModel}
              onPaginationChange={setPaginationModel}
              onDownload={handleDownloadReport}
              isRequestPending={isLoadingReport}
            />
          </Paper>
        )}
      </Container>
    </Fragment>
  );
};

export default ReportsPage;
